import React, {useState, useEffect} from 'react';
import {useMutation} from '@tanstack/react-query';
import DashboardLayout from '../components/layout/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import {useAuth} from '../hooks/useAuth';
import {useAppSelector, useAppDispatch} from '../hooks/redux';
import {selectCurrentPatient, setCurrentPatient} from '../slices/patientSlice';
import {User, Lock, Smartphone, Save, Eye, EyeOff} from 'lucide-react';
import UserService from '../services/userService';

interface UserData {
    id: number;
    elis_id: string;
    firstName: string;
    lastName: string;
    birthday: string;
    gender: string;
    ethnicity: string;
    email: string;
    phone: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    twoFAMode?: string;
    smsEnabled: boolean;
}

interface PasswordData {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface PasswordErrors {
    passwordLength: boolean;
    passwordLetters: boolean;
    passwordNumber: boolean;
    passwordMatch: boolean;
}

const Settings: React.FC = () => {
    const {user} = useAuth();
    const dispatch = useAppDispatch();
    const currentPatient = useAppSelector(selectCurrentPatient);
    const [activeSection, setActiveSection] = useState('personal');

    // Initialize user data from existing auth state (like old implementation)
    const initialUserData: UserData = {
        id: currentPatient?.id || 0,
        elis_id: user?.userInfo?.elis_id || '',
        firstName: currentPatient?.firstName || '',
        lastName: currentPatient?.lastName || '',
        birthday: currentPatient?.birthdate || '',
        gender: user?.userInfo?.gender || '',
        ethnicity: user?.userInfo?.ethnicity || '',
        email: user?.userInfo?.email || '',
        phone: user?.userInfo?.phone || '',
        address1: user?.userInfo?.address1 || '',
        address2: user?.userInfo?.address2 || '',
        city: user?.userInfo?.city || '',
        state: user?.userInfo?.state || '',
        zip: user?.userInfo?.zip || '',
        country: user?.userInfo?.country || 'United States',
        twoFAMode: user?.userInfo?.twoFAMode || '',
        smsEnabled: user?.userInfo?.smsEnabled || false,
    };

    const [userData, setUserData] = useState<UserData>(initialUserData);
    const [passwordData, setPasswordData] = useState<PasswordData>({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>({
        passwordLength: true,
        passwordLetters: true,
        passwordNumber: true,
        passwordMatch: true,
    });
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false,
    });
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // Update userData when auth state changes (like old implementation)
    useEffect(() => {
        // Only update if we have user data
        if (user?.userInfo || currentPatient) {
            const newUserData = {
                id: currentPatient?.id || user?.userInfo?.id || 0,
                elis_id: user?.userInfo?.elis_id || '',
                firstName: currentPatient?.firstName || user?.userInfo?.firstName || '',
                lastName: currentPatient?.lastName || user?.userInfo?.lastName || '',
                birthday: currentPatient?.birthdate || user?.userInfo?.birthday || '',
                gender: user?.userInfo?.gender || '',
                ethnicity: user?.userInfo?.ethnicity || '',
                email: user?.userInfo?.email || '',
                phone: user?.userInfo?.phone || '',
                address1: user?.userInfo?.address1 || '',
                address2: user?.userInfo?.address2 || '',
                city: user?.userInfo?.city || '',
                state: user?.userInfo?.state || '',
                zip: user?.userInfo?.zip || '',
                country: user?.userInfo?.country || 'United States',
                twoFAMode: user?.userInfo?.twoFAMode || '',
                smsEnabled: user?.userInfo?.smsEnabled || false,
            };

            setUserData(newUserData);
        }
    }, [user, currentPatient]);

    // Match old implementation sections
    const sections = [
        {id: 'personal', label: 'Personal Details', icon: User},
        {id: 'security', label: 'Password & Security', icon: Lock},
        // SMS Settings only in development (matches old implementation)
        ...(import.meta.env.DEV ? [{id: 'sms', label: 'SMS Settings', icon: Smartphone}] : []),
    ];

    // User data update mutation (using centralized API)
    const updateUserMutation = useMutation({
        mutationFn: (data: UserData) => UserService.updateUser(data),
        onSuccess: (_, variables) => {
            // 1. Update local auth state userInfo
            if (user?.userInfo) {
                Object.assign(user.userInfo, {
                    firstName: variables.firstName,
                    lastName: variables.lastName,
                    gender: variables.gender,
                    ethnicity: variables.ethnicity,
                    phone: variables.phone,
                    address1: variables.address1,
                    address2: variables.address2,
                    city: variables.city,
                    state: variables.state,
                    zip: variables.zip,
                    country: variables.country,
                    smsEnabled: variables.smsEnabled,
                    birthday: variables.birthday,
                });

                // Update localStorage user data
                try {
                    localStorage.setItem('user', JSON.stringify(user));
                } catch {
                    // Ignore localStorage save errors
                }
            }

            // 2. Update Redux currentPatient state
            const updatedPatient = {
                id: variables.id,
                firstName: variables.firstName,
                lastName: variables.lastName,
                fullName: `${variables.firstName} ${variables.lastName}`,
                birthdate: variables.birthday,
            };
            dispatch(setCurrentPatient(updatedPatient));

            // 3. Update localStorage patient info (for backwards compatibility)
            try {
                localStorage.setItem('patient', JSON.stringify(updatedPatient));
            } catch {
                // Ignore localStorage save errors
            }

            setSuccessMessage('Settings updated successfully!');
            setErrorMessage('');
        },
        onError: () => {
            setErrorMessage('Failed to update settings. Please try again.');
            setSuccessMessage('');
        },
    });

    // Password change mutation (using centralized API)
    const changePasswordMutation = useMutation({
        mutationFn: async (passwords: PasswordData) => {
            // First verify old password
            await UserService.verifyPassword(userData.email, passwords.oldPassword);
            // Then change password
            return await UserService.changePassword(userData.email, passwords.newPassword);
        },
        onSuccess: () => {
            setPasswordData({oldPassword: '', newPassword: '', confirmPassword: ''});
            setPasswordErrors({passwordLength: true, passwordLetters: true, passwordNumber: true, passwordMatch: true});
            setSuccessMessage('Password updated successfully!');
            setErrorMessage('');
        },
        onError: () => {
            setErrorMessage('Failed to update password. Please try again.');
            setSuccessMessage('');
        },
    });

    const handleUserDataChange = (field: keyof UserData, value: string | boolean) => {
        setUserData((prev) => ({...prev, [field]: value}));

        // Phone formatting like old implementation
        if (field === 'phone' && typeof value === 'string') {
            const digitReg = /^[0-9]$/;
            let formattedValue = value;

            if (value.length === 4 || value.length === 8) {
                if (value[value.length - 1] !== '-') {
                    formattedValue = value.substring(0, value.length - 1) + '-' + value[value.length - 1];
                }
            }

            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 12)) {
                return; // Don't update if invalid
            }

            setUserData((prev) => ({...prev, phone: formattedValue}));
        }

        // ZIP code validation
        if (field === 'zip' && typeof value === 'string') {
            const digitReg = /^[0-9]$/;
            if (value !== '' && (!digitReg.test(value[value.length - 1]) || value.length > 5)) {
                return;
            }
        }
    };

    const handlePasswordChange = (field: keyof PasswordData, value: string) => {
        setPasswordData((prev) => ({...prev, [field]: value}));

        if (field === 'newPassword') {
            const uppercaseReg = /[A-Z]/;
            const lowercaseReg = /[a-z]/;
            const numberReg = /[\d]/;

            setPasswordErrors((prev) => ({
                ...prev,
                passwordLetters: !uppercaseReg.test(value) || !lowercaseReg.test(value),
                passwordNumber: !numberReg.test(value),
                passwordLength: value.length <= 8,
            }));
        }

        if (field === 'confirmPassword') {
            setPasswordErrors((prev) => ({
                ...prev,
                passwordMatch: value !== passwordData.newPassword,
            }));
        }
    };

    const handleSaveUserData = () => {
        // Phone validation like old implementation
        const phoneReg = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
        if (!phoneReg.test(userData.phone)) {
            setErrorMessage('Invalid phone format, please use ###-###-####');
            return;
        }

        updateUserMutation.mutate(userData);
    };

    const handleChangePassword = () => {
        if (!passwordData.oldPassword) {
            setErrorMessage('Please enter your current password');
            return;
        }

        if (passwordErrors.passwordLength || passwordErrors.passwordLetters || passwordErrors.passwordNumber) {
            setErrorMessage('Please ensure password meets requirements');
            return;
        }

        if (passwordErrors.passwordMatch) {
            setErrorMessage('Please ensure passwords match');
            return;
        }

        changePasswordMutation.mutate(passwordData);
    };

    return (
        <DashboardLayout>
            <PageHeader title="Account Settings" subtitle="Manage your account preferences and security settings" />

            {/* Success/Error Messages */}
            {(successMessage || errorMessage) && (
                <div
                    className={`mb-6 p-4 rounded-lg ${
                        successMessage
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-red-100 text-red-700 border border-red-200'
                    }`}>
                    {successMessage || errorMessage}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:items-start">
                {/* Settings Navigation */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:sticky lg:top-6">
                    <nav className="space-y-2">
                        {sections.map((section) => {
                            const Icon = section.icon;
                            return (
                                <button
                                    key={section.id}
                                    onClick={() => {
                                        setActiveSection(section.id);
                                        setSuccessMessage('');
                                        setErrorMessage('');
                                    }}
                                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors text-left ${
                                        activeSection === section.id
                                            ? 'bg-mld-primary text-white'
                                            : 'text-mld-text-primary hover:bg-gray-50'
                                    }`}>
                                    <Icon className="h-5 w-5" />
                                    <span className="font-medium">{section.label}</span>
                                </button>
                            );
                        })}
                    </nav>
                </div>

                {/* Settings Content */}
                <div className="lg:col-span-3 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    {activeSection === 'personal' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Personal Details</h3>

                            {/* Basic Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Basic Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            First Name
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.firstName}
                                            onChange={(e) => handleUserDataChange('firstName', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Last Name
                                        </label>
                                        <input
                                            type="text"
                                            value={userData.lastName}
                                            onChange={(e) => handleUserDataChange('lastName', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            value={
                                                userData.birthday
                                                    ? new Date(userData.birthday).toISOString().split('T')[0]
                                                    : ''
                                            }
                                            onChange={(e) => handleUserDataChange('birthday', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Biological Sex
                                        </label>
                                        <select
                                            value={userData.gender}
                                            onChange={(e) => handleUserDataChange('gender', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="">--</option>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Ethnicity
                                        </label>
                                        <select
                                            value={userData.ethnicity}
                                            onChange={(e) => handleUserDataChange('ethnicity', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="">--</option>
                                            <option value="American Indian or Alaska Native">
                                                American Indian or Alaska Native
                                            </option>
                                            <option value="Asian">Asian</option>
                                            <option value="Black or African American">Black or African American</option>
                                            <option value="Hispanic or Latino">Hispanic or Latino</option>
                                            <option value="Native Hawaiian or Other Pacific Islander">
                                                Native Hawaiian or Other Pacific Islander
                                            </option>
                                            <option value="White">White</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Contact Information</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={userData.email}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                                            disabled
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Phone
                                        </label>
                                        <input
                                            type="tel"
                                            placeholder="555-555-5555"
                                            value={userData.phone}
                                            onChange={(e) => handleUserDataChange('phone', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Address Information */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Home Address</h4>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="123 Apple Street"
                                            value={userData.address1}
                                            onChange={(e) => handleUserDataChange('address1', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Address Line 2 <span className="text-gray-400">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Apt 100"
                                            value={userData.address2}
                                            onChange={(e) => handleUserDataChange('address2', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Dallas"
                                                value={userData.city}
                                                onChange={(e) => handleUserDataChange('city', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                State
                                            </label>
                                            <select
                                                value={userData.state}
                                                onChange={(e) => handleUserDataChange('state', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                                <option value="">--</option>
                                                <option value="TX">Texas</option>
                                                <option value="CA">California</option>
                                                <option value="NY">New York</option>
                                                <option value="FL">Florida</option>
                                                {/* Add more states as needed */}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                                ZIP Code
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="10001"
                                                value={userData.zip}
                                                onChange={(e) => handleUserDataChange('zip', e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Country
                                        </label>
                                        <select
                                            value={userData.country}
                                            onChange={(e) => handleUserDataChange('country', e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent">
                                            <option value="United States">United States</option>
                                            <option value="Canada">Canada</option>
                                            {/* Add more countries as needed */}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={handleSaveUserData}
                                disabled={updateUserMutation.isPending}
                                className="flex items-center space-x-2 px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                <Save className="h-4 w-4" />
                                <span>{updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}</span>
                            </button>
                        </div>
                    ) : activeSection === 'security' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">Password & Security</h3>

                            {/* Password Change */}
                            <div className="space-y-4">
                                <h4 className="font-medium text-mld-text-primary">Change Password</h4>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Current Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.old ? 'text' : 'password'}
                                                value={passwordData.oldPassword}
                                                onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords((prev) => ({...prev, old: !prev.old}))}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.old ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.new ? 'text' : 'password'}
                                                value={passwordData.newPassword}
                                                onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPasswords((prev) => ({...prev, new: !prev.new}))}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.new ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {passwordData.newPassword && (
                                            <div className="mt-2 space-y-1 text-xs">
                                                <p
                                                    className={
                                                        passwordErrors.passwordLength
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                    }>
                                                    ✓ At least 9 characters long
                                                </p>
                                                <p
                                                    className={
                                                        passwordErrors.passwordLetters
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                    }>
                                                    ✓ Include uppercase, lowercase letters
                                                </p>
                                                <p
                                                    className={
                                                        passwordErrors.passwordNumber
                                                            ? 'text-red-600'
                                                            : 'text-green-600'
                                                    }>
                                                    ✓ Include at least 1 number
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-mld-text-primary mb-2">
                                            Confirm New Password
                                        </label>
                                        <div className="relative">
                                            <input
                                                type={showPasswords.confirm ? 'text' : 'password'}
                                                value={passwordData.confirmPassword}
                                                onChange={(e) =>
                                                    handlePasswordChange('confirmPassword', e.target.value)
                                                }
                                                className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mld-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPasswords((prev) => ({...prev, confirm: !prev.confirm}))
                                                }
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                                {showPasswords.confirm ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </button>
                                        </div>
                                        {passwordData.confirmPassword && (
                                            <p
                                                className={`mt-1 text-xs ${passwordErrors.passwordMatch ? 'text-red-600' : 'text-green-600'}`}>
                                                ✓ Passwords match
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <button
                                    onClick={handleChangePassword}
                                    disabled={changePasswordMutation.isPending}
                                    className="px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                    {changePasswordMutation.isPending ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>

                            {/* 2FA Section - Only in Development */}
                            {import.meta.env.DEV && (
                                <div className="space-y-4 pt-6 border-t border-gray-200">
                                    <h4 className="font-medium text-mld-text-primary">Two-Factor Authentication</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-mld-text-primary">
                                                    2-Step Authentication
                                                </p>
                                                <p className="text-sm text-gray-600">
                                                    {userData.twoFAMode ? 'Enabled' : 'Not enabled'}
                                                </p>
                                            </div>
                                            <button className="px-4 py-2 text-sm bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors">
                                                {userData.twoFAMode ? 'Manage' : 'Enable'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : activeSection === 'sms' ? (
                        <div className="space-y-6">
                            <h3 className="text-lg font-semibold text-mld-text-primary">SMS Settings</h3>
                            <div className="space-y-4">
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h4 className="font-medium text-mld-text-primary">
                                                Test Result SMS Notification
                                            </h4>
                                            <p className="text-sm text-gray-600">
                                                Receive text messages when your test results are available
                                            </p>
                                        </div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={userData.smsEnabled}
                                                onChange={(e) => handleUserDataChange('smsEnabled', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-mld-primary/25 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-mld-primary"></div>
                                        </label>
                                    </div>
                                </div>
                                <button
                                    onClick={handleSaveUserData}
                                    disabled={updateUserMutation.isPending}
                                    className="px-6 py-2 bg-mld-primary text-white rounded-lg hover:bg-mld-primary/90 transition-colors disabled:opacity-50">
                                    {updateUserMutation.isPending ? 'Saving...' : 'Save SMS Settings'}
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default Settings;
